// UserAvatar — the standard person avatar for every Nexilink "user row".
// Shows the user's profile image when present, falling back to sage initials.
// Used by Medarbetare, Onboarding assigned lists, and Min arbetsplats colleagues
// so a person looks the same wherever they appear.
import React, { useState } from 'react';
import { resolveMediaUrl } from '@/lib/mediaUrl';

const SIZES = {
    sm: 'w-9 h-9 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
} as const;

const initialsOf = (name: string): string =>
    name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';

interface UserAvatarProps {
    name: string;
    pictureUrl?: string | null;
    size?: keyof typeof SIZES;
    className?: string;
}

export const UserAvatar: React.FC<UserAvatarProps> = ({ name, pictureUrl, size = 'md', className = '' }) => {
    const [errored, setErrored] = useState(false);
    const dims = SIZES[size];
    const resolved = resolveMediaUrl(pictureUrl);

    if (resolved && !errored) {
        return (
            <img
                src={resolved}
                alt={name}
                onError={() => setErrored(true)}
                className={`${dims} rounded-full object-cover bg-gray-100 flex-shrink-0 ${className}`}
            />
        );
    }

    return (
        <div className={`${dims} rounded-full bg-[#A4C3B2] flex items-center justify-center font-semibold text-[#1A4D4F] flex-shrink-0 ${className}`}>
            {initialsOf(name)}
        </div>
    );
};

export default UserAvatar;
